// Presentational panels for the integration detail page's connect sidebar,
// split out of IntegrationDetail.tsx (IP-247). The page owns all connect state +
// handlers and passes them in; these just render. Styles live in the shared
// connect.module.css (also imported by the page for its AuthMethod / guided
// blocks), so neither file reaches into the other's CSS module.
import { useState } from "react";
import type { Connection, ConnectionStatus } from "../../../types/integration";
import styles from "./connect.module.css";

const SUPPORT_EMAIL = "support@zerotoclaw.ai";

// IP-247: mirror ztc-core's alias rule (IntegrationConnectService.ALIAS_PATTERN)
// for instant feedback; the server stays authoritative. Letters, numbers, spaces
// and hyphens only — no '_' so the gateway's '__' tool-name separator can't leak
// into a namespace. 32-char cap keeps the namespaced tool name LLM-safe.
const ALIAS_PATTERN = /^[A-Za-z0-9 -]{1,32}$/;

// Per-account card status copy.
const ACCOUNT_STATUS_LABEL: Record<ConnectionStatus, string> = {
  active: "active",
  pending: "connecting…",
  error: "error",
  revoked: "revoked",
  disconnected: "disconnected",
};

// One account card. Owns the inline-rename editor state so the page stays
// stateless about which card is open. Rename is offered (onRename present) only
// in multi-account mode; a lone connection has no name and no rename.
const AccountCard = ({
  connection,
  title,
  connecting,
  busy,
  canReconnect,
  onReconnect,
  onDisconnect,
  onCancel,
  onRename,
}: {
  connection: Connection;
  title: string;
  connecting: boolean;
  busy: boolean;
  canReconnect: boolean;
  onReconnect: (alias: string | null) => void;
  onDisconnect: (connectionId: string) => void;
  onCancel: (connectionId: string) => void;
  onRename?: (connectionId: string, alias: string) => Promise<{ ok: boolean; message?: string }>;
}): JSX.Element => {
  const c = connection;
  const label = c.alias ?? title;
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const trimmed = value.trim();
  const invalid = trimmed !== "" && !ALIAS_PATTERN.test(trimmed);
  const canSave = trimmed !== "" && !invalid && !saving;

  const startEdit = (): void => {
    setValue(c.alias ?? "");
    setError(null);
    setEditing(true);
  };
  const cancelEdit = (): void => {
    setEditing(false);
    setError(null);
  };
  const save = async (): Promise<void> => {
    if (!onRename || !canSave) return;
    setSaving(true);
    const res = await onRename(c.connectionId, trimmed);
    setSaving(false);
    if (res.ok) setEditing(false);
    else setError(res.message ?? "Rename failed.");
  };

  if (editing) {
    return (
      <div className={styles.accountCard}>
        <div className={styles.renameRow}>
          <input
            className={styles.aliasInput}
            value={value}
            autoFocus
            maxLength={32}
            aria-label={`Rename ${label}`}
            aria-invalid={invalid}
            onChange={(e) => {
              setValue(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") void save();
              if (e.key === "Escape") cancelEdit();
            }}
          />
          <button
            type="button"
            className={styles.renameConfirm}
            disabled={!canSave}
            aria-label="Save name"
            title="Save"
            onClick={() => void save()}
          >
            {saving ? "…" : "✓"}
          </button>
          <button
            type="button"
            className={styles.renameCancel}
            disabled={saving}
            aria-label="Cancel rename"
            title="Cancel"
            onClick={cancelEdit}
          >
            ✕
          </button>
        </div>
        {invalid ? (
          <p className={styles.aliasHint}>Letters, numbers, spaces and hyphens only.</p>
        ) : null}
        {error ? <p className={styles.error}>{error}</p> : null}
      </div>
    );
  }

  return (
    <div className={styles.accountCard}>
      <div className={styles.accountHead}>
        <span className={`${styles.statusDot} ${styles[`dot_${c.status}`] ?? ""}`} aria-hidden />
        <span className={styles.accountLabel}>{label}</span>
        {onRename && c.status !== "pending" ? (
          <button type="button" className={styles.renameBtn} disabled={busy} onClick={startEdit}>
            Rename
          </button>
        ) : null}
        <span className={styles.accountStatus}>{ACCOUNT_STATUS_LABEL[c.status]}</span>
      </div>
      <div className={styles.accountActions}>
        {c.status === "pending" ? (
          <button type="button" className={styles.cancelLink} disabled={busy} onClick={() => onCancel(c.connectionId)}>
            {busy ? "Cancelling…" : "Cancel"}
          </button>
        ) : (
          <>
            {canReconnect ? (
              <button
                type="button"
                className={styles.ctaButtonGhost}
                disabled={busy || connecting}
                onClick={() => onReconnect(c.alias)}
              >
                Reconnect
              </button>
            ) : null}
            {c.status !== "disconnected" ? (
              <button
                type="button"
                className={styles.ctaButtonDanger}
                disabled={busy}
                onClick={() => onDisconnect(c.connectionId)}
              >
                {busy ? "Disconnecting…" : "Disconnect"}
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

// IP-247: one card per connected account + the "Add another account" prompt.
// IP-255: rename is offered per card only with >=2 connections (multi-account
// mode); a single connection stays unnamed and simple.
export const ConnectedAccounts = ({
  connections,
  title,
  connectEnabled,
  connecting,
  actioningConnId,
  addingAccount,
  newAlias,
  connectError,
  connectNotice,
  onReconnect,
  onDisconnect,
  onCancel,
  onRename,
  onStartAdd,
  onAliasChange,
  onSubmitAdd,
  onCancelAdd,
}: {
  connections: Connection[];
  title: string;
  connectEnabled: boolean;
  connecting: boolean;
  actioningConnId: string | null;
  addingAccount: boolean;
  newAlias: string;
  connectError: string | null;
  connectNotice: string | null;
  onReconnect: (alias: string | null) => void;
  onDisconnect: (connectionId: string) => void;
  onCancel: (connectionId: string) => void;
  onRename: (connectionId: string, alias: string) => Promise<{ ok: boolean; message?: string }>;
  onStartAdd: () => void;
  onAliasChange: (value: string) => void;
  onSubmitAdd: () => void;
  onCancelAdd: () => void;
}): JSX.Element => {
  // Naming only exists with >=2 connections (matches the backend's RENAME_SINGLE
  // guard); a single connection is the plain, unnamed view.
  const canRename = connections.length >= 2;
  return (
  <div className={styles.connectBlock}>
    <span className={styles.ctaEyebrow}>// CONNECTED ACCOUNTS</span>
    <div className={styles.accountCards}>
      {connections.map((c) => (
        <AccountCard
          key={c.connectionId}
          connection={c}
          title={title}
          connecting={connecting}
          busy={actioningConnId === c.connectionId}
          canReconnect={connectEnabled}
          onReconnect={onReconnect}
          onDisconnect={onDisconnect}
          onCancel={onCancel}
          {...(canRename ? { onRename } : {})}
        />
      ))}
    </div>
    {connectEnabled ? (
      addingAccount ? (
        (() => {
          const trimmed = newAlias.trim();
          const aliasInvalid = trimmed !== "" && !ALIAS_PATTERN.test(trimmed);
          return (
            <div className={styles.addAccount}>
              <input
                className={styles.aliasInput}
                value={newAlias}
                autoFocus
                maxLength={32}
                placeholder="Account name (e.g. Work)"
                aria-invalid={aliasInvalid}
                onChange={(e) => onAliasChange(e.target.value)}
              />
              {aliasInvalid ? (
                <p className={styles.aliasHint}>
                  Letters, numbers, spaces and hyphens only.
                </p>
              ) : null}
              <div className={styles.connectActions}>
                <button
                  type="button"
                  className={styles.ctaButton}
                  disabled={connecting || trimmed === "" || aliasInvalid}
                  onClick={onSubmitAdd}
                >
                  Add account <span aria-hidden>→</span>
                </button>
                <button type="button" className={styles.addCancelBtn} onClick={onCancelAdd}>
                  Cancel
                </button>
              </div>
            </div>
          );
        })()
      ) : (
        <button type="button" className={styles.ctaButtonGhost} onClick={onStartAdd}>
          + Add another account
        </button>
      )
    ) : null}
    {connectError ? <p className={styles.error}>{connectError}</p> : null}
    {connectNotice ? <p className={styles.notice}>{connectNotice}</p> : null}
  </div>
  );
};

// First-connect CTA (no accounts connected yet).
export const ConnectCta = ({
  title,
  isApiKey,
  apiKeyCta,
  connecting,
  connectionStatus,
  connectError,
  connectNotice,
  onConnect,
  onCancel,
}: {
  title: string;
  isApiKey: boolean;
  // The API-key line, from apiKeyConnectCopy (email asks for a mailbox password).
  apiKeyCta: string;
  connecting: boolean;
  connectionStatus: ConnectionStatus | null;
  connectError: string | null;
  connectNotice: string | null;
  onConnect: () => void;
  onCancel: () => void;
}): JSX.Element => (
  <div className={styles.connectBlock}>
    <span className={styles.ctaEyebrow}>// CONNECT</span>
    {connectionStatus === "pending" && !isApiKey ? (
      <>
        <p className={styles.ctaText}>
          Finish authorising {title} in the other tab. This page will update on its own as soon as
          it's done.
        </p>
        <button type="button" className={styles.ctaButton} disabled>
          Connecting…
        </button>
        <button type="button" className={styles.cancelLink} onClick={onCancel}>
          Cancel
        </button>
      </>
    ) : (
      <>
        <p className={styles.ctaText}>
          {isApiKey
            ? apiKeyCta
            : `Authorise ${title} so your agent can use it.`}
        </p>
        <button type="button" className={styles.ctaButton} onClick={onConnect} disabled={connecting}>
          {connecting
            ? "Starting…"
            : connectionStatus === "error" ||
                connectionStatus === "revoked" ||
                connectionStatus === "disconnected"
              ? `Reconnect ${title}`
              : `Connect ${title}`}
          <span aria-hidden>→</span>
        </button>
        {connectionStatus === "error" ? (
          <p className={styles.error}>
            That didn't go through. Try again — if it keeps failing, email{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we'll sort it out.
          </p>
        ) : connectionStatus === "revoked" ? (
          <p className={styles.error}>
            Your access to {title} was revoked upstream. Reconnect to keep using it — if this keeps
            happening, email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
        ) : connectionStatus === "disconnected" ? (
          <p className={styles.ctaText}>
            Previously connected — reconnect to restore the integration.
          </p>
        ) : null}
        {connectNotice ? <p className={styles.notice}>{connectNotice}</p> : null}
        {connectError ? <p className={styles.error}>{connectError}</p> : null}
      </>
    )}
  </div>
);

// Register-interest form (integration not connectable today).
export const InterestForm = ({
  title,
  userRegistered,
  interestedCount,
  note,
  submitting,
  error,
  onNoteChange,
  onRegister,
}: {
  title: string;
  userRegistered: boolean;
  interestedCount: number;
  note: string;
  submitting: boolean;
  error: string | null;
  onNoteChange: (value: string) => void;
  onRegister: () => void;
}): JSX.Element => (
  <div className={styles.interestBlock}>
    <span className={styles.ctaEyebrow}>// INTEREST</span>
    {userRegistered ? (
      <>
        <p className={styles.ctaText}>
          <strong>Interest registered.</strong> We'll reach out as soon as we pick this up.
        </p>
        {interestedCount > 1 ? (
          <p className={styles.ctaCount}>{interestedCount} customers asking for this so far.</p>
        ) : null}
      </>
    ) : (
      <>
        <p className={styles.ctaText}>Tell us you want this and we'll prioritise it.</p>
        <label className={styles.label}>
          <span>Anything specific? (optional)</span>
          <textarea
            className={styles.textarea}
            value={note}
            onChange={(e) => onNoteChange(e.target.value)}
            placeholder={`How would you use ${title}?`}
            rows={3}
            maxLength={1000}
          />
        </label>
        <button
          type="button"
          className={styles.ctaButton}
          onClick={onRegister}
          disabled={submitting}
        >
          {submitting ? "Sending…" : "Register interest"}
          <span aria-hidden>→</span>
        </button>
        {error ? <p className={styles.error}>{error}</p> : null}
        {interestedCount > 0 ? (
          <p className={styles.ctaCount}>{interestedCount} customers waiting on this.</p>
        ) : null}
      </>
    )}
  </div>
);
